import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Undo2, Wallet, Loader2, X } from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

function dataBR(s?: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('pt-BR')
}

function useClientes() {
  return useQuery<any[]>({
    queryKey: ['lk-clientes-rel'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'cliente', limit: 500 } })).data,
  })
}

const inputCls = 'rounded-lg border border-ink-200 px-2.5 py-1.5 text-sm bg-white'
function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  )
}

// ===================== RELATÓRIO DE DEVOLUÇÕES =====================
export function RelDevolucoes() {
  const { data: clientes = [] } = useClientes()
  const [dataIni, setDataIni] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [clienteId, setClienteId] = useState('')
  const [destino, setDestino] = useState('')

  const params = {
    data_ini: dataIni || undefined, data_fim: dataFim || undefined,
    cliente_id: clienteId || undefined, destino: destino || undefined,
  }
  const { data, isFetching } = useQuery<any>({
    queryKey: ['rel-devolucoes', params],
    queryFn: async () => (await api.get('/vendas/relatorios/devolucoes', { params })).data,
  })
  const itens = data?.itens || []
  const limpar = () => { setDataIni(''); setDataFim(''); setClienteId(''); setDestino('') }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Undo2 className="text-rose-600" size={22} />
        <h1 className="text-xl font-bold text-ink-900">Relatório de Devoluções</h1>
      </div>
      <p className="text-sm text-ink-500 mb-4">Vendas devolvidas — geram nota de entrada e estorno financeiro (crédito ou restituição).</p>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-ink-200 p-3 mb-4 flex flex-wrap gap-3 items-end">
        <Campo label="De"><input type="date" className={inputCls} value={dataIni} onChange={e => setDataIni(e.target.value)} /></Campo>
        <Campo label="Até"><input type="date" className={inputCls} value={dataFim} onChange={e => setDataFim(e.target.value)} /></Campo>
        <Campo label="Cliente">
          <select className={inputCls} value={clienteId} onChange={e => setClienteId(e.target.value)}>
            <option value="">Todos</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </Campo>
        <Campo label="Destino do valor pago">
          <select className={inputCls} value={destino} onChange={e => setDestino(e.target.value)}>
            <option value="">Todos</option>
            <option value="credito">Crédito do cliente</option>
            <option value="abater">Restituição (a pagar)</option>
          </select>
        </Campo>
        <button onClick={limpar} className="text-xs text-ink-500 hover:text-ink-800 flex items-center gap-1 pb-1.5">
          <X size={13} /> Limpar
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4 max-w-md">
        <div className="bg-white rounded-xl border border-ink-200 p-4">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Devoluções</div>
          <div className="text-2xl font-bold text-ink-900">{data?.qtd ?? 0}</div>
        </div>
        <div className="bg-white rounded-xl border border-ink-200 p-4">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Valor devolvido</div>
          <div className="text-2xl font-bold text-rose-600">{formatMoney(data?.total_valor || 0)}</div>
        </div>
      </div>

      {isFetching ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ink-400" /></div>
      ) : itens.length === 0 ? (
        <div className="text-center text-ink-400 py-10 bg-white rounded-xl border border-ink-200">Nenhuma devolução no filtro.</div>
      ) : (
        <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-4 py-2">Pedido</th>
                <th className="text-left px-4 py-2">Cliente</th>
                <th className="text-left px-4 py-2">Motivo</th>
                <th className="text-left px-4 py-2">Destino</th>
                <th className="text-left px-4 py-2">Nota entrada</th>
                <th className="text-right px-4 py-2">Valor</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((d: any) => (
                <tr key={d.pedido_id} className="border-t border-ink-100">
                  <td className="px-4 py-2 text-ink-600">{dataBR(d.data)}</td>
                  <td className="px-4 py-2 font-medium">#{d.numero}</td>
                  <td className="px-4 py-2">{d.cliente_nome || '—'}</td>
                  <td className="px-4 py-2 text-ink-500 text-xs">{d.motivo || '—'}</td>
                  <td className="px-4 py-2">
                    {d.destino === 'credito' ? <span className="text-emerald-700 text-xs bg-emerald-50 px-1.5 py-0.5 rounded">Crédito</span>
                     : d.destino === 'abater' ? <span className="text-amber-700 text-xs bg-amber-50 px-1.5 py-0.5 rounded">Restituição</span>
                     : <span className="text-ink-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-ink-500">{d.nota_entrada_numero ? `nº ${d.nota_entrada_numero} (${d.nota_entrada_status})` : '—'}</td>
                  <td className="px-4 py-2 text-right font-medium">{formatMoney(d.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ===================== RELATÓRIO DE CRÉDITO DE CLIENTE =====================
export function RelCreditoCliente() {
  const { data: clientes = [] } = useClientes()
  const [view, setView] = useState<'saldos' | 'movimentos'>('saldos')
  const [dataIni, setDataIni] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [clienteId, setClienteId] = useState('')
  const [tipo, setTipo] = useState('')
  const [origem, setOrigem] = useState('')

  const { data: saldos = [], isFetching: loadingSaldos } = useQuery<any[]>({
    queryKey: ['rel-creditos-saldos'],
    queryFn: async () => (await api.get('/financeiro/creditos')).data,
    enabled: view === 'saldos',
  })
  const params = {
    data_ini: dataIni || undefined, data_fim: dataFim || undefined,
    pessoa_id: clienteId || undefined, tipo: tipo || undefined, origem: origem || undefined,
  }
  const { data: extrato, isFetching: loadingExtrato } = useQuery<any>({
    queryKey: ['rel-creditos-extrato', params],
    queryFn: async () => (await api.get('/financeiro/creditos-extrato', { params })).data,
    enabled: view === 'movimentos',
  })
  const totalSaldos = saldos.reduce((s, l) => s + Number(l.saldo || 0), 0)
  const limpar = () => { setDataIni(''); setDataFim(''); setClienteId(''); setTipo(''); setOrigem('') }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Wallet className="text-brand-600" size={22} />
        <h1 className="text-xl font-bold text-ink-900">Crédito de Clientes</h1>
      </div>
      <p className="text-sm text-ink-500 mb-4">
        Saldo de haver e movimentos (gerados em devoluções).{' '}
        <Link to="/financeiro/creditos" className="text-brand-600 underline">Gerenciar no Financeiro →</Link>
      </p>

      <div className="flex gap-2 mb-4">
        {(['saldos', 'movimentos'] as const).map(v => (
          <button key={v} onClick={() => setView(v)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium ${view === v ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
            {v === 'saldos' ? 'Saldos por cliente' : 'Movimentos'}
          </button>
        ))}
      </div>

      {view === 'saldos' ? (
        <>
          <div className="bg-white rounded-xl border border-ink-200 p-4 mb-4 inline-block">
            <div className="text-xs text-ink-500 uppercase tracking-wide">Crédito total em aberto</div>
            <div className="text-2xl font-bold text-brand-600">{formatMoney(totalSaldos)}</div>
          </div>
          {loadingSaldos ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ink-400" /></div>
           : saldos.length === 0 ? <div className="text-center text-ink-400 py-10 bg-white rounded-xl border border-ink-200">Nenhum cliente com crédito.</div>
           : (
            <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-ink-500 text-xs uppercase">
                  <tr><th className="text-left px-4 py-2">Cliente</th><th className="text-right px-4 py-2">Saldo</th></tr>
                </thead>
                <tbody>
                  {saldos.map(l => (
                    <tr key={l.pessoa_id} className="border-t border-ink-100">
                      <td className="px-4 py-2 font-medium">{l.nome}</td>
                      <td className="px-4 py-2 text-right font-bold text-brand-600">{formatMoney(l.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Filtros do extrato */}
          <div className="bg-white rounded-xl border border-ink-200 p-3 mb-4 flex flex-wrap gap-3 items-end">
            <Campo label="De"><input type="date" className={inputCls} value={dataIni} onChange={e => setDataIni(e.target.value)} /></Campo>
            <Campo label="Até"><input type="date" className={inputCls} value={dataFim} onChange={e => setDataFim(e.target.value)} /></Campo>
            <Campo label="Cliente">
              <select className={inputCls} value={clienteId} onChange={e => setClienteId(e.target.value)}>
                <option value="">Todos</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </Campo>
            <Campo label="Tipo">
              <select className={inputCls} value={tipo} onChange={e => setTipo(e.target.value)}>
                <option value="">Todos</option>
                <option value="entrada">Entrada (gerou)</option>
                <option value="uso">Uso (consumiu)</option>
              </select>
            </Campo>
            <Campo label="Origem">
              <select className={inputCls} value={origem} onChange={e => setOrigem(e.target.value)}>
                <option value="">Todas</option>
                <option value="devolucao">Devolução</option>
                <option value="abate_titulo">Abate de título</option>
                <option value="baixa">Baixa</option>
                <option value="venda">Venda</option>
              </select>
            </Campo>
            <button onClick={limpar} className="text-xs text-ink-500 hover:text-ink-800 flex items-center gap-1 pb-1.5">
              <X size={13} /> Limpar
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4 max-w-2xl">
            <div className="bg-white rounded-xl border border-ink-200 p-3">
              <div className="text-[11px] text-ink-500 uppercase">Entradas</div>
              <div className="text-xl font-bold text-emerald-600">{formatMoney(extrato?.total_entrada || 0)}</div>
            </div>
            <div className="bg-white rounded-xl border border-ink-200 p-3">
              <div className="text-[11px] text-ink-500 uppercase">Usos</div>
              <div className="text-xl font-bold text-rose-600">{formatMoney(extrato?.total_uso || 0)}</div>
            </div>
            <div className="bg-white rounded-xl border border-ink-200 p-3">
              <div className="text-[11px] text-ink-500 uppercase">Saldo do período</div>
              <div className="text-xl font-bold text-brand-600">{formatMoney(extrato?.saldo_periodo || 0)}</div>
            </div>
          </div>

          {loadingExtrato ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ink-400" /></div>
           : (extrato?.itens || []).length === 0 ? <div className="text-center text-ink-400 py-10 bg-white rounded-xl border border-ink-200">Nenhum movimento no filtro.</div>
           : (
            <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-ink-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Data</th>
                    <th className="text-left px-4 py-2">Cliente</th>
                    <th className="text-left px-4 py-2">Tipo</th>
                    <th className="text-left px-4 py-2">Origem</th>
                    <th className="text-left px-4 py-2">Descrição</th>
                    <th className="text-right px-4 py-2">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {extrato.itens.map((m: any) => (
                    <tr key={m.id} className="border-t border-ink-100">
                      <td className="px-4 py-2 text-ink-600">{dataBR(m.criado_em)}</td>
                      <td className="px-4 py-2">{m.cliente_nome || '—'}</td>
                      <td className="px-4 py-2">
                        {m.tipo === 'entrada' ? <span className="text-emerald-700 text-xs bg-emerald-50 px-1.5 py-0.5 rounded">Entrada</span>
                         : <span className="text-rose-700 text-xs bg-rose-50 px-1.5 py-0.5 rounded">Uso</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-ink-500">{m.origem}</td>
                      <td className="px-4 py-2 text-xs text-ink-500">{m.descricao || '—'}</td>
                      <td className={`px-4 py-2 text-right font-medium ${m.tipo === 'entrada' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {m.tipo === 'entrada' ? '+' : '−'} {formatMoney(m.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
